/**
 * Provider de contexto de sessão (Rede e Loja)
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  saveSessionContext,
  loadSessionContext,
  saveToContextHistory,
  getLastValidContext,
  clearSessionContext,
  clearContextHistory,
  SessionContext as StoredContext,
} from '@/services/sessionStorage';
import {
  getUserScope,
  validateStoreOperable,
  getAccessibleStores,
  UserScope,
} from '@/utils/scopeValidator';
import {
  logModeSwitch,
  logStoreSwitch,
  logActingAsStore,
} from '@/services/contextAudit';
import { getUserNetwork } from '@/utils/scopeValidator';
import { updateInterceptorContext } from '@/services/api/contextInterceptor';
import { toast } from 'sonner';
import { CONTEXT_ERRORS } from '@/constants/contextErrors';

export type ContextMode = 'NETWORK' | 'STORE';

export interface SessionContextState {
  networkId: string | null;
  storeId: string | null;
  contextMode: ContextMode;
  actingAsStoreId: string | null;
  userScope: UserScope | null;
  isLoading: boolean;
  isInitialized: boolean;
}

export interface SessionContextActions {
  setStore: (storeId: string | null) => Promise<void>;
  setContextMode: (mode: ContextMode) => Promise<void>;
  setActingAsStore: (storeId: string | null) => void;
  switchToNetworkMode: () => Promise<void>;
  switchToStoreMode: (storeId?: string) => Promise<void>;
  clearContext: () => void;
  validateCurrentContext: () => Promise<boolean>;
}

export type SessionContextValue = SessionContextState & SessionContextActions;

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export const SessionContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useSupabaseAuth();
  const queryClient = useQueryClient();

  const [state, setState] = useState<SessionContextState>({
    networkId: null,
    storeId: null,
    contextMode: 'NETWORK',
    actingAsStoreId: null,
    userScope: null,
    isLoading: true,
    isInitialized: false,
  });

  // Carregar contexto inicial ao montar ou quando usuário mudar
  useEffect(() => {
    const initializeContext = async () => {
      // console.log('🔄 SessionContext.initializeContext - User:', user?.id);
      if (!user?.id) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitialized: false,
          networkId: null,
          storeId: null,
          contextMode: 'NETWORK',
        }));
        /* console.log('🔄 SessionContext - User is null, ending initialization'); */
        return;
      }

      try {
        // console.log('🔄 SessionContext - Iniciando carregamento do contexto para userId:', user.id);
        
        // Tentar carregar último contexto do localStorage
        const lastContext = getLastValidContext(user.id);
        // console.log('📦 SessionContext - Contexto carregado do localStorage:', lastContext);

        if (lastContext?.networkId) {
          // Verificar se a rede está ativa antes de validar escopo
          const { data: network, error: networkError } = await supabase
            .from('networks')
            .select('id, is_active')
            .eq('id', lastContext.networkId)
            .maybeSingle();

          if (networkError || !network) {
            /* console.warn('⚠️ SessionContext - Rede não encontrada ou inativa no localStorage:', {
              networkId: lastContext.networkId,
              error: networkError
            }); */

            // Verificar se o usuário ainda tem a role antes de limpar tudo
            const { data: roleCheck } = await supabase
              .from('user_network_roles')
              .select('id')
              .eq('user_id', user.id)
              .eq('network_id', lastContext.networkId)
              .eq('is_active', true)
              .maybeSingle();

            if (!roleCheck) {
              /* console.log('🧹 SessionContext - Role não encontrada, procedendo com limpeza'); */
              clearSessionContext();
              clearContextHistory(user.id);
            } else {
              /* console.log('🛡️ SessionContext - Role encontrada, permitindo restauração do contexto'); */
              // Proceder como se a rede existisse para validar o escopo
            }
          } else if (network && !network.is_active) {
            
            // Buscar rede do usuário novamente
            const userNetwork = await getUserNetwork(user.id);
            if (userNetwork) {
              const scope = await getUserScope(user.id, userNetwork.networkId);
              if (scope) {
                setState(prev => ({
                  ...prev,
                  networkId: userNetwork.networkId,
                  storeId: null,
                  contextMode: 'NETWORK',
                  userScope: scope,
                  isLoading: false,
                  isInitialized: true,
                }));
                updateInterceptorContext({
                  networkId: userNetwork.networkId,
                  storeId: null,
                  contextMode: 'NETWORK',
                });
                return;
              }
            }
            
            // Se não encontrou rede ativa, deixar vazio
            setState(prev => ({
              ...prev,
              networkId: null,
              storeId: null,
              contextMode: 'NETWORK',
              userScope: null,
              isLoading: false,
              isInitialized: true,
            }));
            return;
          }

          // Validar se o contexto ainda é válido
          const scope = await getUserScope(user.id, lastContext.networkId);
          // console.log('🔍 SessionContext - Scope validado:', scope);

          if (scope) {
            // Se tinha storeId, validar acesso
            if (lastContext.storeId) {
              // console.log('🏪 SessionContext - Validando acesso ao storeId:', lastContext.storeId);
              const hasAccess = await validateStoreOperable(
                user.id,
                lastContext.networkId,
                lastContext.storeId
              );
              // console.log('✓ SessionContext - Tem acesso à loja?', hasAccess);

              if (hasAccess) {
                // Restaurar contexto completo
                // console.log('✅ SessionContext - Restaurando contexto completo');
                setState(prev => ({
                  ...prev,
                  networkId: lastContext.networkId,
                  storeId: lastContext.storeId,
                  contextMode: lastContext.contextMode,
                  userScope: scope,
                  isLoading: false,
                  isInitialized: true,
                }));

                updateInterceptorContext({
                  networkId: lastContext.networkId,
                  storeId: lastContext.storeId,
                  contextMode: lastContext.contextMode,
                });

                // console.log('🎉 SessionContext - Contexto restaurado com sucesso');
                return;
              } else {
                /* console.warn('⚠️ SessionContext - Usuário não tem mais acesso à loja armazenada'); */
              }
            }

            // Restaurar apenas network
            // console.log('🌐 SessionContext - Restaurando apenas networkId (sem loja)');
            setState(prev => ({
              ...prev,
              networkId: lastContext.networkId,
              storeId: null,
              contextMode: 'NETWORK',
              userScope: scope,
              isLoading: false,
              isInitialized: true,
            }));

            updateInterceptorContext({
              networkId: lastContext.networkId,
              storeId: null,
              contextMode: 'NETWORK',
            });

            return;
          }
        }

        // Buscar a rede única do usuário
        // console.log('🔍 SessionContext - Sem contexto válido, buscando rede do usuário');
        const userNetwork = await getUserNetwork(user.id);

        if (userNetwork) {
          console.log('🌐 SessionContext - Rede encontrada:', userNetwork);
          const scope = await getUserScope(user.id, userNetwork.networkId);

          if (scope) {
            // Auto-selecionar a rede única
            setState(prev => ({
              ...prev,
              networkId: userNetwork.networkId,
              storeId: null,
              contextMode: 'NETWORK',
              userScope: scope,
              isLoading: false,
              isInitialized: true,
            }));

            updateInterceptorContext({
              networkId: userNetwork.networkId,
              storeId: null,
              contextMode: 'NETWORK',
            });

            // console.log('✅ SessionContext - Contexto inicial configurado com rede');
            return;
          }
        }

        // Se não encontrou rede, deixar vazio
        /* console.warn('⚠️ SessionContext - Usuário sem rede associada'); */
        setState(prev => ({
          ...prev,
          networkId: null,
          storeId: null,
          contextMode: 'NETWORK',
          userScope: null,
          isLoading: false,
          isInitialized: true,
        }));
      } catch (error) {
        console.error('❌ SessionContext - Erro ao inicializar contexto:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitialized: true,
        }));
      }
    };

    initializeContext();
  }, [user?.id, queryClient]);

  // Salvar contexto sempre que mudar
  useEffect(() => {
    if (state.isInitialized && user?.id && state.networkId) {
      const contextToSave: StoredContext = {
        networkId: state.networkId,
        storeId: state.storeId,
        contextMode: state.contextMode,
        actingAsStoreId: state.actingAsStoreId,
        timestamp: Date.now(),
      };

      saveSessionContext(contextToSave);
      saveToContextHistory(user.id, contextToSave);

      updateInterceptorContext({
        networkId: state.networkId,
        storeId: state.storeId,
        actingAsStoreId: state.actingAsStoreId,
        contextMode: state.contextMode,
      });
    }
  }, [state.networkId, state.storeId, state.contextMode, state.actingAsStoreId, state.isInitialized, user?.id]);

  const setStore = useCallback(async (storeId: string | null) => {
    if (storeId) {
      /* console.log('🏪 SessionContext.setStore - Selecionando loja:', storeId); */
    }
    
    if (!user?.id || !state.networkId) {
      /* console.warn('⚠️ SessionContext.setStore - Sem networkId ou userId'); */
      return;
    }

    try {
      if (storeId) {
        /* console.log('🔍 SessionContext.setStore - Validando acesso à loja:', storeId); */
        // Validar acesso à loja
        const hasAccess = await validateStoreOperable(user.id, state.networkId, storeId);
        /* console.log('✓ SessionContext.setStore - Tem acesso?', hasAccess); */
        
        if (!hasAccess) {
          /* console.error('❌ SessionContext.setStore - Acesso negado à loja'); */
          toast.error(CONTEXT_ERRORS.SCOPE_VIOLATION);
          return;
        }

        const previousStoreId = state.storeId;
        console.log('🔄 SessionContext.setStore - Atualizando estado para STORE mode');

        setState(prev => ({
          ...prev,
          storeId,
          contextMode: 'STORE',
          actingAsStoreId: null,
        }));

        // Invalidar queries relacionadas à loja anterior
        queryClient.invalidateQueries({ queryKey: ['store'] });

        // Registrar auditoria
        await logStoreSwitch(state.networkId, previousStoreId, storeId, window.location.pathname);

        // console.log('✅ SessionContext.setStore - Loja alterada com sucesso');
        toast.success('Loja alterada com sucesso');
      } else {
        // Limpar loja (voltar para modo rede)
        // console.log('🔄 SessionContext.setStore - Voltando para NETWORK mode');
        setState(prev => ({
          ...prev,
          storeId: null,
          contextMode: 'NETWORK',
          actingAsStoreId: null,
        }));
      }
    } catch (error) {
      console.error('❌ SessionContext.setStore - Erro ao mudar loja:', error);
      toast.error('Erro ao alterar loja');
    }
  }, [user?.id, state.networkId, state.storeId, queryClient]);

  const setContextMode = useCallback(async (mode: ContextMode) => {
    if (!user?.id || !state.networkId) return;

    try {
      const previousMode = state.contextMode;

      if (mode === 'STORE' && !state.storeId) {
        // Modo STORE requer uma loja selecionada
        toast.error(CONTEXT_ERRORS.NO_STORE);
        return;
      }

      setState(prev => ({
        ...prev,
        contextMode: mode,
        // Limpar acting_as ao mudar modo
        actingAsStoreId: null,
      }));

      // Registrar auditoria
      await logModeSwitch(
        state.networkId,
        previousMode,
        mode,
        state.storeId,
        window.location.pathname
      );
    } catch (error) {
      console.error('Erro ao mudar modo:', error);
      toast.error('Erro ao alterar modo');
    }
  }, [user?.id, state.networkId, state.contextMode, state.storeId]);

  const switchToNetworkMode = useCallback(async () => {
    await setContextMode('NETWORK');
    setState(prev => ({
      ...prev,
      storeId: null,
      actingAsStoreId: null,
    }));
  }, [setContextMode]);

  const switchToStoreMode = useCallback(async (storeId?: string) => {
    if (storeId) {
      await setStore(storeId);
    } else if (state.storeId) {
      await setContextMode('STORE');
    } else {
      toast.error(CONTEXT_ERRORS.NO_STORE);
    }
  }, [setStore, setContextMode, state.storeId]);

  const setActingAsStore = useCallback((actingAsStoreId: string | null) => {
    if (!user?.id || !state.networkId) return;

    setState(prev => ({
      ...prev,
      actingAsStoreId,
    }));

    if (actingAsStoreId && state.storeId) {
      logActingAsStore(
        state.networkId,
        state.storeId,
        actingAsStoreId,
        'ACTING_AS_STORE',
        window.location.pathname
      );
    }
  }, [user?.id, state.networkId, state.storeId]);

  const clearContext = useCallback(() => {
    // console.log('🧹 SessionContext.clearContext - Limpando todo o contexto');
    
    setState(prev => ({
      ...prev,
      networkId: null,
      storeId: null,
      contextMode: 'NETWORK',
      actingAsStoreId: null,
      userScope: null,
    }));

    // Limpar localStorage
    clearSessionContext();
    if (user?.id) {
      clearContextHistory(user.id);
    }

    updateInterceptorContext({
      networkId: null,
      storeId: null,
      contextMode: 'NETWORK',
    });
  }, [user?.id]);

  const validateCurrentContext = useCallback(async (): Promise<boolean> => {
    if (!user?.id || !state.networkId) return false;

    try {
      // Verificar se a rede está ativa
      const { data: network, error: networkError } = await supabase
        .from('networks')
        .select('is_active')
        .eq('id', state.networkId)
        .single();

      if (networkError || !network?.is_active) {
        /* console.warn('⚠️ validateCurrentContext - Rede inativa, limpando contexto'); */
        toast.error(CONTEXT_ERRORS.NETWORK_INACTIVE);
        clearContext();
        clearSessionContext();
        clearContextHistory(user.id);
        return false;
      }

      // Validar network
      const scope = await getUserScope(user.id, state.networkId);
      if (!scope) {
        toast.error(CONTEXT_ERRORS.INVALID_NETWORK);
        clearContext();
        clearSessionContext();
        clearContextHistory(user.id);
        return false;
      }

      // Validar store se houver
      if (state.storeId) {
        const hasAccess = await validateStoreOperable(user.id, state.networkId, state.storeId);
        if (!hasAccess) {
          toast.error(CONTEXT_ERRORS.INVALID_STORE);
          // Voltar para modo rede
          setState(prev => ({
            ...prev,
            storeId: null,
            contextMode: 'NETWORK',
            actingAsStoreId: null,
          }));
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Erro ao validar contexto:', error);
      return false;
    }
  }, [user?.id, state.networkId, state.storeId, clearContext]);

  const value: SessionContextValue = {
    ...state,
    setStore,
    setContextMode,
    setActingAsStore,
    switchToNetworkMode,
    switchToStoreMode,
    clearContext,
    validateCurrentContext,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext = (): SessionContextValue => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within SessionContextProvider');
  }
  return context;
};
